import { useState, useEffect, useCallback, useMemo } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { PAGE_SIZE } from '../../constants/pagination';
import { useClinicSwitch } from '../../hooks/useClinicSwitch';

// Date-range presets now live in one place, shared with Encounters. Re-exported
// here so existing `from './useAccounting'` imports keep working.
export { presetRange, customRange, translatedPresets } from '../../constants/datePresets';

// ── Query ─────────────────────────────────────────────────────────────────────

// One page size for every server-paginated list (DESIGN-CONCEPT §9.1) — re-exported
// so existing `from './useAccounting'` call sites keep resolving it.
export { PAGE_SIZE };

const REPORT_QUERY = `
  query DoctorAccountingReport(
    $fromTs: Float, $toTs: Float, $clinicId: String,
    $itemType: AccountingItemType, $settlement: SettlementFilter,
    $search: String, $page: Int, $staffPage: Int, $pageSize: Int
  ) {
    doctorAccountingReport(
      fromTs: $fromTs, toTs: $toTs, clinicId: $clinicId,
      itemType: $itemType, settlement: $settlement,
      search: $search, page: $page, staffPage: $staffPage, pageSize: $pageSize
    ) {
      grossTotal
      doctorShareTotal
      collectedDoctorShare
      totalsByCurrency { currency grossTotal doctorShareTotal collectedDoctorShare }
      mixedCurrency
      itemCount
      linesTotal
      staffLinesTotal
      page
      staffPage
      pageSize
      clinics {
        clinicId clinicName currency grossTotal doctorShareTotal collectedDoctorShare
        visitCount medicalServiceCount productCount
      }
      types { itemType currency count grossTotal doctorShareTotal }
      lines {
        id invoiceId clinicId clinicName patientId patientName currency createdAt
        itemType description quantity grossAmount patientPayable
        doctorShare settlementStatus performedByDoctor
      }
      staffLines {
        id invoiceId clinicId clinicName patientId patientName currency createdAt
        itemType description quantity grossAmount patientPayable
        doctorShare settlementStatus performedByDoctor
      }
    }
  }
`;

// Every amount in the report is minor units of its own row's `currency`. The
// three scalar totals are NULL when the lines span more than one currency — read
// `totalsByCurrency` (lib/clinicMoney's `reportTotals`), never the scalars.
const EMPTY = {
  grossTotal: 0, doctorShareTotal: 0, collectedDoctorShare: 0,
  totalsByCurrency: [], mixedCurrency: false,
  itemCount: 0, linesTotal: 0, staffLinesTotal: 0,
  page: 1, staffPage: 1, pageSize: PAGE_SIZE,
  clinics: [], types: [], lines: [], staffLines: [],
};

/**
 * The doctor's accounting report for a window + filters + page.
 *
 * Everything — including the free-text search — is server-side: the KPI cards, the
 * breakdowns, the table pages and the CSV all describe exactly the same set of rows.
 * Aggregates cover the whole filtered window; only the two line lists are paginated.
 */
export function useAccounting({ range, clinicId, itemType, settlement, search, page, staffPage }) {
  const [report, setReport]   = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [nonce, setNonce]     = useState(0);

  const vars = useMemo(() => ({
    fromTs: range?.fromTs ?? 0,
    toTs: range?.toTs ?? 0,
    clinicId: clinicId || null,
    itemType: itemType || null,
    settlement: settlement || 'ALL',
    search: (search || '').trim() || null,
    page: page || 1,
    staffPage: staffPage || 1,
    pageSize: PAGE_SIZE,
  }), [range?.fromTs, range?.toTs, clinicId, itemType, settlement, search, page, staffPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    bffQuery(REPORT_QUERY, vars)
      .then((data) => {
        if (cancelled) return;
        setReport(data?.doctorAccountingReport || EMPTY);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('doctorAccountingReport failed:', err);
        // The error OBJECT, not `err.message`. bffClient stamps the failure kind
        // onto it, and flattening to a string here threw that away — which is
        // why an offline doctor was told the accounting report was unavailable
        // and offered a retry button that could not work.
        setError(err);
        setReport(EMPTY);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vars, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { report, loading, error, reload, vars };
}

/**
 * Every line in the current filtered window, unpaginated — the CSV export.
 * `pageSize: 0` asks the BFF for the whole set; the two lists come back merged and
 * re-sorted so the file reads like the old single table did.
 */
export async function fetchAllReportLines(vars) {
  const data = await bffQuery(REPORT_QUERY, { ...vars, page: 1, staffPage: 1, pageSize: 0 });
  const r = data?.doctorAccountingReport;
  return [...(r?.lines || []), ...(r?.staffLines || [])].sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * The clinics this doctor works at — the clinic filter's options, `{ id, name,
 * currency }`.
 *
 * Every clinic the doctor OWNS plus every clinic they are an active MEMBER of: the
 * same list the clinic switch and پذیرش's clinic chip use. This used to be
 * `listClinics`, which clinicmanagement filters to clinics this account owns, so
 * a doctor who works at someone else's clinic could not isolate it — and once
 * clinics keep books in different currencies, isolating one clinic is how a
 * doctor sees one currency's figures on their own.
 */
export function useMyClinics() {
  return useClinicSwitch().targets;
}
